"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export const useThrottle = <T>(
  value: T,
  interval = 500,
  resetKey?: unknown,
): T => {
  const [throttledValue, setThrottledValue] = useState<T>(value);
  // eslint-disable-next-line react-hooks/purity -- Date.now() is used only once as initial value for this ref
  const lastExecuted = useRef<number>(Date.now());
  const lastValue = useRef<T>(value);
  const hasAppliedLeadingNonFalsyValue = useRef(Boolean(value));

  const syncImmediately = useCallback((nextValue: T) => {
    setThrottledValue(nextValue);
    lastExecuted.current = Date.now();
    lastValue.current = nextValue;
    hasAppliedLeadingNonFalsyValue.current = Boolean(nextValue);
  }, []);

  useEffect(() => {
    syncImmediately(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetKey changes should flush the latest render's value immediately without re-running on every value update
  }, [resetKey, syncImmediately]);

  useEffect(() => {
    if (!hasAppliedLeadingNonFalsyValue.current && Boolean(value)) {
      syncImmediately(value);
      return;
    }

    const handler = setTimeout(
      () => {
        if (Date.now() - lastExecuted.current >= interval) {
          setThrottledValue(lastValue.current);
          lastExecuted.current = Date.now();
        }
      },
      Math.max(0, interval - (Date.now() - lastExecuted.current)),
    );

    lastValue.current = value;

    return () => {
      clearTimeout(handler);
    };
  }, [value, interval, syncImmediately]);

  return throttledValue;
};
